import { Box, Button, Grid } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { downloadContractFiles } from '../../../api/contracts';
import { deleteContract, getContracts } from '../../../redux/slices/contracts';
import { useDispatch, useSelector } from '../../../redux/store';
import ConfirmDialog from '../../../reusable-components/confirm-dialog/ConfirmDialog';
import { ACTIVE, PREV_OWNER, RENEW, TERMINATED } from '../../../reusable-components/datagrid/contractColumns';
import CustomDataGrid from '../../../reusable-components/datagrid/CustomDataGrid';
import Iconify from '../../../reusable-components/iconify/Iconify';
import { useScopeCheck } from '../../../reusable-components/scopes';
import { download } from '../../../utils/download';
import ContractTablePDFPreview from './ContractTablePDFPreview';

export default function ContractsView() {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    //Active|Terminated|Previous Owner|Pending Renew
    const contractStatus = matchPath('/dashboard/contracts/:status', pathname)?.params?.status ?? null;

    const dispatch = useDispatch();
    const { contracts = [], isLoading, hasLoadedContracts } = useSelector((state) => state.contracts || {});

    const [isSelectingContracts, setIsSelectingContracts] = useState(false);
    const [selectedContractIds, setSelectedContractIds] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);

    const canEdit = useScopeCheck(['Contracts-Edit', 'Contracts-Admin'], true, true);

    const data = useMemo(() => contractStatus === 'active' ?
        contracts.filter(x => (!x.isTerminated && (x.terminationEffectiveDate == null || x.terminationEffectiveDate > new Date())
            && (!x.previousOwnership || x.assumed)))
        : contractStatus === 'terminated' ?
            contracts.filter(x => (x.isTerminated || (!!x.terminationEffectiveDate && x.terminationEffectiveDate <= new Date()))
                && (!x.previousOwnership || x.assumed))
            : contractStatus === 'previous-owner' ? contracts.filter(x => x.previousOwnership)
                : contractStatus === 'pending-renew' ? contracts.filter(x => ((!x.isTerminated
                    && (!x.terminationEffectiveDate || x.terminationEffectiveDate > new Date())) && !!x.renewalDate && x.renewalDate < new Date()))
                    : [], [contracts, contractStatus]);

    const actionButtons = {
        field: 'actions',
        headerName: 'Actions',
        disableReorder: true,
        flex: 0.8,
        minWidth: 90,
        renderCell: (params) => {
            const { id } = params.row;
            return (
                <>
                    {canEdit &&
                        <ConfirmDialog
                            icon="delete-outline"
                            title="Delete"
                            color="error"
                            content={`Are you sure you want to delete this contract?`}
                            actionButton="Delete"
                            disabled={params?.row && !params?.row?.isEditable}
                            action={() => dispatch(deleteContract(id))}
                        />
                    }
                    <ContractTablePDFPreview id={id} hasFile={!!params.row.contractLetterFileName} hasTerminationFile={!!params.row.terminationLetterFileName} />
                </>
            );
        },
        id: 'actions',
    };

    const columns = [...(contractStatus === 'active' ? ACTIVE : contractStatus === 'terminated' ? TERMINATED
        : contractStatus === 'pending-renew' ? RENEW : /* contractStatus === 'previous-owner' ? */ PREV_OWNER), actionButtons];

    const rowClick = useCallback((id) => {
        navigate(`/dashboard/contracts/${id}?status=${contractStatus}`);
    }, [navigate, contractStatus]);

    const handleDownload = useCallback(async () => {
        if (!isSelectingContracts) {
            setIsSelectingContracts(true);
            return;
        }
        const contractIds = [...selectedContractIds];
        setIsSelectingContracts(false);
        setSelectedContractIds([]);
        if (contractIds.length === 0) {
            enqueueSnackbar('Select at least one contract to download.', { variant: 'error' });
            return;
        }
        setIsDownloading(true);
        try {
            const response = await downloadContractFiles(contractIds);
            if (response.data.type.includes('json')) {
                const result = JSON.parse(await response.data.text());
                enqueueSnackbar(result.message || 'Failed to download contract files.', { variant: 'error' });
                return;
            }
            download(response.data, 'Contracts.zip');
            enqueueSnackbar('Contract files download started.', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to download contract files.', { variant: 'error' });
        } finally {
            setIsDownloading(false);
        }
    }, [isSelectingContracts, selectedContractIds]);

    const cancelDownload = () => {
        setIsSelectingContracts(false);
        setSelectedContractIds([]);
        setIsDownloading(false);
    }

    const CustomToolbar = useCallback(
        () => (
            <Grid container direction="row" justifyContent="flex-start" alignItems="center" sx={{ mt: 1 }}>
                <Box>
                    {canEdit &&
                        <Button
                            variant="contained"
                            color="secondary"
                            sx={{ mr: 1 }}
                            size="small"
                            onClick={() => rowClick(`new`)}
                            startIcon={<Iconify icon="eva:plus-outline" />}
                        >
                            Add Contract
                        </Button>
                    }
                    <Button
                        variant="outlined"
                        color="secondary"
                        sx={{ mr: 1 }}
                        size="small"
                        onClick={handleDownload}
                        disabled={isDownloading}
                        startIcon={<Iconify icon="file-icons:microsoft-excel" />}
                    >
                        {isSelectingContracts ? 'Confirm Download' : 'Download Contracts'}
                    </Button>
                    {isSelectingContracts &&
                        <Button
                            variant="outlined"
                            color="error"
                            sx={{ mr: 1 }}
                            size="small"
                            onClick={cancelDownload}
                        >
                            Cancel download
                        </Button>
                    }
                </Box>
            </Grid>
        ),
        [rowClick, handleDownload, isSelectingContracts, isDownloading]
    );

    useEffect(() => {
        if (!hasLoadedContracts)
            dispatch(getContracts());
    }, [dispatch, hasLoadedContracts]);


    return (contractStatus &&
        <Box>
            <CustomDataGrid
                key={`contracts-grid-${contractStatus}`}
                gridId={`contracts-${contractStatus}-table`}
                gridColumns={columns}
                data={data}
                isLoading={isLoading}
                CustomLeftToolbar={CustomToolbar}
                boxSX={{ height: 'calc(100vh - 200px)' }}
                checkboxSelection={isSelectingContracts}
                rowSelectionModel={selectedContractIds}
                onRowSelectionModelChange={setSelectedContractIds}
                keepNonExistentRowsSelected
                enableRowClick={isSelectingContracts}
                onRowClick={(params, event) => {
                    const actionsCellIsClicked = event?.target?.closest?.('[data-field="actions"]');
                    const cellTextIsSelected = window.getSelection().toString()?.length > 0;

                    if (isSelectingContracts || actionsCellIsClicked || cellTextIsSelected) return;

                    rowClick(params.row.id);
                }}
                sort={[{ field: 'dateAdded', sort: 'desc' }]}
                defaultHiddenColumns={['autoRenew', 'invoiceEvery', 'paymentTerms']}
            />
        </Box>
    );
}
